import React, { useState, useEffect, useRef,Suspense, lazy } from "react";
import Dropdown from "../../../Components/CustomDropdown/customdropdown";
import { useToast } from "../../../Utils/toastContext";
import { useDispatch, useSelector } from "react-redux";
import ConfirmContinueButton from "../../../Components/ContinueButton/continue-button";
import { updateFormData } from "../../../Redux/store/onboardingFormSlice";
import { useNavigate, useLocation } from "react-router-dom";
import AddUserPopup from "../../../Components/add-joint-accounts/Add-User-Popup";
import Select, { components } from "react-select";
import { Plus, Minus } from "lucide-react";
import ConfirmResetButton from "../../../Components/ContinueButton/reset-button";
import ReactFlow, { Controls, Background } from "reactflow";
import "reactflow/dist/style.css";
const validator = require("validator");

const ShareholderLayer = ({
  layerName,
  shareholdersForLayer = [],
  onAddLayer,
  collapsed = false,
  onToggle,
}) => {
  const isOpen = !collapsed; // fully controlled
  const dispatch = useDispatch();

  // First layer shareholders
  const [shareholders, setShareholders] = useState([
    {
      id: 1,
      name: "Ann",
      type: "Individual",
      shares: 20,
      email: "f@n.com",
    },
    {
      id: 2,
      name: "XYZ",
      type: "Corporate",
      shares: 40,
      email: "f@n.com",
    },
    {
      id: 3,
      name: "Carol",
      type: "Trust",
      shares: 40,
      email: "f@n.com",
    },
  ]);
  const [isFormValid, setIsFormValid] = useState(false);

  const [activeTab, setActiveTab] = useState(0);
  // Controls state
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  // Sub-layer shareholders (per tab)
  const [layerData, setLayerData] = useState(() => {
    let initial = {};
    shareholdersForLayer.forEach((s, idx) => {
      initial[idx] = [
        {
          id: Date.now(),
          name: "",
          type: "Individual",
          shares: "",
          email: "",
          errors: {},
        },
      ];
    });
    return { [layerName]: initial };
  });

  const calculateSharesCounter = (list) => {
    const total = list.reduce((acc, s) => acc + Number(s.shares || 0), 0);
    return `${total.toFixed(2)}`;
  };
  const buildApiPayload = () => {
    // Root layer
    const root = {
      layerName,
      shareholders: shareholders.map((s, idx) => {
        const base = {
          id: s.id,
          name: s.name,
          type: s.type,
          shares: s.shares,
          email: s.email,
        };

        // If this shareholder has a sub-layer, attach children
        if (layerData[layerName]?.[idx]?.length > 0) {
          base.children = [
            {
              layerName: `${s.name} Layer`,
              shareholders: layerData[layerName][idx].map((child) => ({
                id: child.id,
                name: child.name,
                type: child.type,
                shares: child.shares,
                email: child.email,
              })),
            },
          ];
        }

        return base;
      }),
    };

    return root;
  };

  /** ----------------------
   * Root Layer Handlers
   * ---------------------- */
  const updateRootShareholder = (id, field, value, errors = {}) => {
    setShareholders((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [field]: value, errors } : s))
    );
  };

  const addRootShareholder = () => {
    setShareholders((prev) => [
      ...prev,
      { id: Date.now(), name: "", type: "Individual", shares: 0, email: "" },
    ]);
  };

  const removeRootShareholder = (id) => {
    setShareholders((prev) => prev.filter((s) => s.id !== id));
  };
  useEffect(() => {
    setActiveTab(0); // reset tab index when layer data changes
  }, [shareholdersForLayer]);
  useEffect(() => {
    if (shareholders.length > 0) {
      dispatch(updateFormData({ field: "shareholders", value: shareholders }));
    }
  }, [shareholders]);
  useEffect(() => {
    if (layerData != null) {
      dispatch(updateFormData({ field: "layerData", value: layerData }));
    }
  }, [layerData]);
  /** ----------------------
   * Sub-layer Handlers
   * ---------------------- */
  const updateShareholder = (tabIdx, idx, field, value, errors = {}) => {
    setLayerData((prev) => {
      const copy = { ...prev };

      // Ensure layerName exists
      if (!copy[layerName]) copy[layerName] = {};

      // Ensure tabIdx exists
      if (!Array.isArray(copy[layerName][tabIdx])) {
        copy[layerName][tabIdx] = [];
      }

      // Clone current tab data
      const tabData = [...copy[layerName][tabIdx]];
      const current = tabData[idx] || {};

      // Ensure value is safe for string fields
      tabData[idx] = {
        ...current,
        [field]: typeof value === "string" ? value : "",
        errors,
      };

      // Assign back
      copy[layerName] = { ...copy[layerName], [tabIdx]: tabData };

      return copy;
    });
  };

  const addShareholder = (tabIdx) => {
    setLayerData((prev) => {
      const copy = { ...prev };
      copy[layerName][tabIdx] = [
        ...(copy[layerName][tabIdx] || []),
        {
          id: Date.now(),
          name: "",
          type: "Individual",
          shares: "",
          email: "",
          errors: {},
        },
      ];
      return copy;
    });
  };

  const removeShareholder = (tabIdx, id) => {
    setLayerData((prev) => {
      const copy = { ...prev };
      copy[layerName][tabIdx] = copy[layerName][tabIdx].filter(
        (s) => s.id !== id
      );
      return copy;
    });
  };

  /** ----------------------
   * Fill Application
   * ---------------------- */
  // inside ShareholderLayer
  const handleFillApplication = (tabIdx = null) => {
    let qualified = [];

    if (shareholdersForLayer.length === 0) {
      // Root layer → pick only Corporate/Trust with shares >= 25
      qualified = shareholders.filter(
        (s) => (s.type === "Corporate" || s.type === "Trust") && s.shares >= 25
      );
    } else {
      // Sub-layers → collect from all tabs
      Object.values(layerData[layerName]).forEach((tabShareholders = []) => {
        const tabQualified = tabShareholders.filter(
          (s) =>
            (s.type === "Corporate" || s.type === "Trust") && s.shares >= 25
        );
        qualified = [...qualified, ...tabQualified];
      });
    }

    if (qualified.length > 0) {
      // 1) Clear layerData forward from this layer

      // 2) Trim layers forward and add fresh next layer
      onAddLayer(qualified, layerName, layerName);
    }
  };

  useEffect(() => {
    if (!shareholders.length) {
      setIsFormValid(false);
      return;
    }

    const allFilled = shareholders.every(
      (s) =>
        s.name.trim() !== "" &&
        s.type.trim() !== "" &&
        s.email.trim() !== "" &&
        !s.errors?.email &&
        parseFloat(s.shares) > 0
    );

    const totalShares = calculateSharesCounter(shareholders);
    if (allFilled && Number(totalShares) === 100.0) {
      setIsFormValid(true);
    } else {
      setIsFormValid(false);
    }
  }, [shareholders]);
  const isLayerFormValid = (tabIdx) => {
    const list = layerData[layerName][tabIdx] || [];
    if (!list.length) return false;

    // All rows filled (and no field errors)
    const allFilled = list.every(
      (s) =>
        s.name.trim() !== "" &&
        s.type.trim() !== "" &&
        s.email.trim() !== "" &&
        !s.errors?.email &&
        parseFloat(s.shares) > 0
    );

    // Shares must equal 100
    const totalShares = calculateSharesCounter(list);

    return allFilled && Number(totalShares).toFixed(2) === "100.00";
  };

  /** ----------------------
   * Render Controls Section
   * ---------------------- */
  const Controls = ({ list, isFirst, tabIdx }) => {
    const numShareholders = list.length;
    const shareCounter = calculateSharesCounter(list);

    return (
      <div className="font-medium flex flex-wrap items-center justify-between px-4 py-3 text-sm">
        {/* Show Records */}
        <div className="flex items-center gap-2">
          <label className="text-[#8C8C8C]">Show Records</label>
          <select
            value={recordsPerPage}
            onChange={(e) => setRecordsPerPage(Number(e.target.value))}
            className="border rounded-lg px-2 py-1 text-black focus:outline-none focus:ring-1 focus:ring-gray-300"
          >
            <option>10</option>
            <option>20</option>
            <option>50</option>
          </select>
        </div>

        {/* Search */}
        <div className="relative">
          <input
            type="text"
            className="p-2 pl-8 border rounded-xl"
            placeholder="Search Shareholder"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <span className="absolute left-2 top-3 text-gray-500">
            {" "}
            <img src="/search-normal.svg" alt="Company Logo" className="mr-2" />
          </span>
        </div>
        {/* Shareholders Count */}
        {isFirst && (
          <div className="flex items-center gap-2">
            <span className="font-medium text-[#464646]">
              NO OF SHAREHOLDERS
            </span>
            <div className="font-semibold">
              <div className="flex items-center text-sm mt-1 md:mt-0 space-x-0 ml-1 border  rounded-md bg-white shadow-md">
                {/* Remove last row */}
                <button
                  onClick={() => {
                    setShareholders((prev) => {
                      if (prev.length === 0) return prev; // avoid error if no rows
                      const lastId = prev[prev.length - 1].id;
                      return prev.filter((s) => s.id !== lastId);
                    });
                  }}
                  className="text-black px-2"
                >
                  <Minus size={18} />
                </button>

                {/* Count display */}
                <span className="text-sm font-medium bg-black text-white p-2">
                  {shareholders.length.toString().padStart(2, "0")}
                </span>

                {/* Add new row */}
                <button
                  onClick={addRootShareholder}
                  className="text-black px-2"
                >
                  <Plus size={18} />
                </button>
              </div>
            </div>
          </div>
        )}
        {!isFirst && (
          <div className="flex items-center gap-2">
            <span className="font-medium text-[#464646]">
              NO OF SHAREHOLDERS
            </span>

            <div className="font-semibold">
              <div className="flex items-center text-sm mt-1 md:mt-0 space-x-0 ml-1 border rounded-md bg-white shadow-md">
                {/* Remove last row */}
                <button
                  onClick={() => {
                    setLayerData((prev) => {
                      const copy = { ...prev };
                      const list = copy[layerName][tabIdx] || [];
                      if (list.length === 0) return prev; // nothing to remove
                      const lastId = list[list.length - 1].id;
                      copy[layerName][tabIdx] = list.filter(
                        (s) => s.id !== lastId
                      );
                      return copy;
                    });
                  }}
                  className="text-black px-2"
                >
                  <Minus size={18} />
                </button>

                {/* Count display */}
                <span className="text-sm font-medium bg-black text-white px-3 py-1">
                  {(layerData[layerName]?.[tabIdx]?.length || 0)
                    .toString()
                    .padStart(2, "0")}
                </span>

                {/* Add new row */}
                <button
                  onClick={() => addShareholder(tabIdx)}
                  className="text-black px-2"
                >
                  <Plus size={18} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Shares Counter */}
        <div className="flex items-center gap-2 font-medium">
          <span className=" text-[#464646]">SHARES COUNTER</span>
          <div
            className={`${
              shareCounter != 100.0 ? "text-red-600" : "text-green-600"
            }
          border rounded-lg px-3 py-1  font-semibold`}
          >
            {shareCounter}%
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="border rounded-2xl shadow-sm bg-white mb-6">
      {/* Header */}
      <div
        onClick={onToggle}
        className="bg-[#005A9C] text-white px-4 py-2 rounded-t-2xl flex justify-between items-center cursor-pointer"
      >
        <h2 className="text-sm font-medium">{layerName}</h2>
        <span className="text-lg">{isOpen ? "−" : "+"}</span>
      </div>

      {isOpen && (
        <>
          {/* CASE 1: Root Layer (no tabs) */}
          {shareholdersForLayer.length === 0 && (
            <div className="p-4 border rounded-b-lg">
              {/* Controls */}
              <Controls list={shareholders} isFirst={true} />
              {calculateSharesCounter(shareholders) != 100.0 && (
                <div className=" flex mx-5 my-2 text-center items-center justify-center  rounded-md px-4 py-2 text-red-500 bg-red-100 border border-red-500">
                  <div className="flex  items-center">
                    <img
                      src="/danger.svg"
                      alt="Arrow"
                      className=" left-0 w-5 h-5 mr-2"
                    />{" "}
                    Missing shareholder information or incorrect percentages
                    entered. Please ensure all shareholder's share percentages
                    are entered accurately until the "Shares Counter" reaches
                    100%.
                  </div>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-[#8C8C8C]">
                  <thead className="">
                    <tr className="border-[#EDEDED] border font-medium">
                      <th className="p-2 text-left font-medium"></th>
                      <th className="p-2 text-left font-medium">
                        Shareholder Name
                      </th>
                      <th className="p-2 text-left font-medium">
                        Type Of Shareholder
                      </th>
                      <th className="p-2 text-left font-medium">Shares</th>
                      <th className="p-2 text-left font-medium">
                        Email Address
                      </th>
                      <th className="p-2 text-left font-medium">
                        User Registration
                      </th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {shareholders.map((s, i) => (
                      <tr key={s.id} className="border-t font-medium">
                        <td className="p-2 text-[#8C8C8C]">
                          {String(i + 1).padStart(2, "0")}.
                        </td>
                        <td className="p-2 text-black">
                          <input
                            className="border w-full p-2 rounded-xl text-sm"
                            value={s.name}
                            onChange={(e) =>
                              updateRootShareholder(
                                s.id,
                                "name",
                                e.target.value
                              )
                            }
                          />
                        </td>
                        <td className="p-2 text-black">
                          <select
                            className="w-full border p-2 rounded-xl text-sm"
                            value={s.type}
                            onChange={(e) =>
                              updateRootShareholder(
                                s.id,
                                "type",
                                e.target.value
                              )
                            }
                          >
                            <option value="Individual">Individual</option>
                            <option value="Corporate">Corporate</option>
                            <option value="Trust">Trust</option>
                          </select>
                        </td>
                        <td className="p-2 text-black">
                          <div className="relative w-24">
                            <input
                              type="number"
                              value={s.shares}
                              onChange={(e) =>
                                updateRootShareholder(
                                  s.id,
                                  "shares",
                                  e.target.value
                                )
                              }
                              onBlur={(e) => {
                                let value = parseFloat(e.target.value) || 0;
                                updateRootShareholder(
                                  s.id,
                                  "shares",
                                  value.toFixed(2)
                                );
                              }}
                              placeholder="10.00"
                              className={`p-2 pr-8 text-black w-full font-medium border rounded-xl text-md transition-all duration-200 ${"border-gray-300"}`}
                            />
                            <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 text-sm">%</span>
                            </div>
                          </div>
                        </td>
                        <td className="p-2 text-black">
                          <input
                            type="email"
                            className={` ${
                              s.errors?.email ? "border-[#FF5050]" : ""
                            }
                            border w-full p-2 rounded-xl text-sm`}
                            value={s.email}
                            onChange={(e) => {
                              const value = e.target.value;
                              const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                                value
                              );
                              updateRootShareholder(
                                s.id,
                                "email",
                                e.target.value,
                                {
                                  ...s.errors,
                                  email:
                                    isValid || value === ""
                                      ? ""
                                      : "Invalid email",
                                }
                              );
                            }}
                          />
                          {s.errors?.email && (
                            <span className="text-red-500 text-xs">
                              {s.errors.email}
                            </span>
                          )}
                        </td>
                        <td className="p-2">
                          {s.type !== "Individual" && s.shares >= 25 ? (
                            <button className="px-3 py-1 rounded-lg shadow-md border text-black font-medium">
                              Fill Application
                            </button>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="p-2 text-right">
                          <button
                            onClick={() => removeRootShareholder(s.id)}
                            className="text-gray-400 hover:text-red-600"
                          >
                            <img
                              src="/trash.svg"
                              alt="Delete"
                              className="h-4 w-4"
                            />{" "}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Add Shareholder */}
              <div className="px-4 py-3 flex justify-center ">
                <button
                  onClick={addRootShareholder}
                  className="flex items-center gap-2 text-[#005A9C] rounded-md font-medium text-sm hover:underline border-2 border-dashed p-2"
                >
                  <img
                    src="/add-darkBlue.svg"
                    alt="Add Icon"
                    className="w-5 h-5"
                  />
                  Add Shareholder
                </button>
              </div>
              <div className="flex justify-end px-4 py-4">
                <button
                  onClick={() => handleFillApplication()}
                  disabled={!isFormValid}
                  className={`px-6 py-2 rounded-full text-white ${
                    isFormValid
                      ? "bg-black hover:bg-gray-900"
                      : "bg-gray-300 cursor-not-allowed"
                  }`}
                >
                  Save
                </button>
              </div>
            </div>
          )}

          {/* CASE 2: Sub-Layers (with tabs) */}
          {shareholdersForLayer.length > 0 && (
            <div className="">
              {/* Tabs */}
              <div className="flex  border-b">
                {shareholdersForLayer.map((s, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveTab(idx)}
                    className={`px-4 py-2 text-sm font-medium ${
                      activeTab === idx
                        ? "bg-black text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
              {/* Controls */}
              <Controls
                list={layerData[layerName][activeTab] || []}
                isFirst={false}
                tabIdx={activeTab}
              />
              {/* Tab Content */}
              {calculateSharesCounter(layerData[layerName][activeTab] || []) !=
                100.0 && (
                <div className=" flex mx-5 my-2 text-center items-center justify-center  rounded-md px-4 py-2 text-red-500 bg-red-100 border border-red-500">
                  <div className="flex  items-center">
                    <img
                      src="/danger.svg"
                      alt="Arrow"
                      className=" left-0 w-5 h-5 mr-2"
                    />{" "}
                    Missing shareholder information or incorrect percentages
                    entered. Please ensure all shareholder's share percentages
                    are entered accurately until the "Shares Counter" reaches
                    100%.
                  </div>
                </div>
              )}
              <div className="p-4 border rounded-b-lg">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-gray-700">
                    <thead className="">
                      <tr className="border-[#EDEDED] border font-medium">
                        <th className="p-2 text-left font-medium"></th>
                        <th className="p-2 text-left font-medium">
                          Shareholder Name
                        </th>
                        <th className="p-2 text-left font-medium">
                          Type Of Shareholder
                        </th>
                        <th className="p-2 text-left font-medium">Shares</th>
                        <th className="p-2 text-left font-medium">
                          Email Address
                        </th>
                        <th className="p-2 text-left font-medium">
                          User Registration
                        </th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(layerData[layerName][activeTab] || []).map((s, idx) => (
                        <tr key={s.id} className="border-t font-medium">
                          <td className="p-2 text-[#8C8C8C]">
                            {String(idx + 1).padStart(2, "0")}.
                          </td>
                          <td className="p-2 text-black">
                            <input
                              className="border w-full p-2 rounded-xl text-sm"
                              value={s.name}
                              onChange={(e) =>
                                updateShareholder(
                                  activeTab,
                                  idx,
                                  "name",
                                  e.target.value
                                )
                              }
                            />
                          </td>
                          <td className="p-2 text-black">
                            <select
                              className="w-full border p-2 rounded-xl text-sm"
                              value={s.type}
                              onChange={(e) =>
                                updateShareholder(
                                  activeTab,
                                  idx,
                                  "type",
                                  e.target.value
                                )
                              }
                            >
                              <option value="Individual">Individual</option>
                              <option value="Corporate">Corporate</option>
                              <option value="Trust">Trust</option>
                            </select>
                          </td>
                          <td className="p-2 text-black">
                            <div className="relative w-24">
                              <input
                                type="number"
                                value={s.shares}
                                onChange={(e) =>
                                  updateShareholder(
                                    activeTab,
                                    idx,
                                    "shares",
                                    e.target.value
                                  )
                                }
                                onBlur={(e) => {
                                  let value = parseFloat(e.target.value) || 0;
                                  updateShareholder(
                                    activeTab,
                                    idx,
                                    "shares",
                                    value.toFixed(2)
                                  );
                                }}
                                placeholder="10.00"
                                className={`p-2 pr-8 text-black w-full font-medium border rounded-xl text-md transition-all duration-200 ${"border-gray-300"}`}
                              />
                              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                                <span className="text-gray-500 text-sm">%</span>
                              </div>
                            </div>
                          </td>
                          <td className="p-2 text-black">
                            <input
                              type="email"
                              className={` ${
                                s.errors?.email ? "border-[#FF5050]" : ""
                              }
                            border w-full p-2 rounded-xl text-sm`}
                              value={s.email}
                              onChange={(e) => {
                                const value = e.target.value;
                                const isValid =
                                  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
                                updateShareholder(
                                  activeTab,
                                  idx,
                                  "email",
                                  value,
                                  {
                                    ...s.errors,
                                    email:
                                      isValid || value === ""
                                        ? ""
                                        : "Invalid email",
                                  }
                                );
                              }}
                            />
                            {s.errors?.email && (
                              <span className="text-red-500 text-xs">
                                {s.errors.email}
                              </span>
                            )}
                          </td>
                          <td className="p-2">
                            {s.type !== "Individual" && s.shares >= 25 ? (
                              <button className="px-3 py-1 rounded-lg shadow-md border text-black font-medium">
                                Fill Application
                              </button>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="p-2 text-right">
                            <button
                              onClick={() => removeShareholder(activeTab, s.id)}
                              className="text-gray-400 hover:text-red-600"
                            >
                              <img
                                src="/trash.svg"
                                alt="Delete"
                                className="h-4 w-4"
                              />{" "}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Add Shareholder */}
                <div className="px-4 py-3 flex justify-center">
                  <button
                    onClick={() => addShareholder(activeTab)}
                    className="flex items-center gap-2 text-[#005A9C] rounded-md font-medium text-sm hover:underline border-2 border-dashed p-2"
                  >
                    <img
                      src="/add-darkBlue.svg"
                      alt="Add Icon"
                      className="w-5 h-5"
                    />
                    Add Shareholder
                  </button>
                </div>
                <div className="flex justify-end px-4 py-4">
                  <button
                    onClick={() => handleFillApplication(activeTab)}
                    disabled={!isLayerFormValid(activeTab)}
                    className={`px-6 py-2 rounded-full text-white ${
                      isLayerFormValid(activeTab)
                        ? "bg-black hover:bg-gray-900"
                        : "bg-gray-300  cursor-not-allowed"
                    }`}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const DirectorInfo = ({ setCurrentStep }) => {
  const formData = useSelector((state) => state.form.formData);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showVisualLink, setShowVisualLink] = useState(false);
  const [accountType, setAccountType] = useState(1);
  const [jointAccountUser, setJointAccountUser] = useState(1);
  const [accountTypeLabel, setAccountTypeLabel] = useState("Individual");
  const [refNumber, setRefNumber] = useState(new Array(7).fill(""));
  const [advisorId, setAdvisorId] = useState("");
  const [isAdvisorExist, setIsAdvisorExist] = useState(true);
  const [advisorName, setAdvisorName] = useState("Brian	Vaughan");
  const [fullName, setfullName] = useState("");
  const [formerNames, setFormerNames] = useState([]);
  const [newFormerName, setFormerName] = useState("");
  const [recommendedBy, setRecommendedBy] = useState("");
  const [DoI, setDoI] = useState("");
  const [currencyData, setCurrencyData] = useState([]);
  const [preselectedcashCurrency, setpreselectedCurrency] = useState({
    value: 2,
    label: "AUD",
  });
  const [preselectedAccountType, setpreselectedAccountType] = useState({
    value: 1,
    label: "Australian Domestic Company",
  });
  const [collapsedLayers, setCollapsedLayers] = useState({}); // { layerName: true/false }
  const [minimumInvestment, setminimumInvestment] = useState(0);
  const typeColors = {
  Corporate: "linear-gradient(135deg,#34d399,#059669)", // green
  Individual: "linear-gradient(135deg,#60a5fa,#3b82f6)", // blue
  Trust: "linear-gradient(135deg,#f472b6,#ec4899)",      // pink
  Default: "linear-gradient(135deg,#a78bfa,#7c3aed)"     // purple fallback
};
function getNodeStyle(node) {
  const layerColors = ["#f59e0b", "#3b82f6", "#10b981", "#ef4444"];

  // Safely calculate the background gradient
  const color =
    layerColors[
      (node.data.layer ?? 0) % layerColors.length
    ]; // fallback to 0 if layer is missing

  const background = `linear-gradient(135deg, ${color}, #000)`; // gradient from color → black

  return {
    background,
    borderRadius: 12,
    padding: "12px 16px",
    color: "white",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    fontWeight: 600,
    fontSize: 14,
    textAlign: "center",
    minWidth: 140,
    transition: "transform 0.2s ease",
  };
}

  const [data, setData] = useState([
    {
      name: "ABC Company",
      type: "Corporate",
      children: [
        { name: "Ann", type: "Individual", children: [] },
        {
          name: "XYZ",
          type: "Corporate",
          children: [
            {
              name: "DEF",
              type: "Corporate",
              children: [
                {
                  name: "Emily Johanson",
                  type: "Individual",
                  children: [],
                },
                { name: "Dalia limited", type: "Corporate", children: [] },
                { name: "Noah", type: "Individual", children: [] },
              ],
            },
            { name: "Aron", type: "Individual", children: [] },
          ],
        },
        { name: "Carol", type: "Trust", children: [] },
      ],
    },
    {
      name: "You trust",
      type: "Trust",
      children: [
        {
          name: "Subsidiary A",
          type: "Corporate",
          children: [
            { name: "John Doe", type: "Individual", children: [] },
            { name: "Jane Smith", type: "Individual", children: [] },
          ],
        },
        { name: "Subsidiary B", type: "Trust", children: [] },
      ],
    },
    {
      name: "John Doe",
      type: "Individual",
      children: [],
    },
  ]);

  const handleCollapseAll = () => {
    const newState = {};
    layers.forEach((l) => (newState[l.name] = true)); // true = collapsed
    setCollapsedLayers(newState);
  };

  const handleExpandAll = () => {
    const newState = {};
    layers.forEach((l) => (newState[l.name] = false)); // false = expanded
    setCollapsedLayers(newState);
  };
  const [signatoryTypeLabel, setSignatoryTypeLabel] = useState("");
  const [signatoryType, setSignatoryType] = useState(1);
  const [signatoriesData, setSignatoriesData] = useState([]);
  const { showToast } = useToast();
  const [fileAttorney, setFileAttorney] = useState(null);
  const [isUserPopOpen, setisUserPopOpen] = useState(false);
  const [actualData, setactualData] = useState([]);
  const [signatoryText, setSignatoryText] = useState("");
  const [signatoryId, setSignatoryId] = useState("");
  const [limitDurationId, setLimitDurationId] = useState(1);
  const [limitDurationLabel, setLimitDurationLabel] = useState("Per Day");

  const [preSelectedSignatoryType, setPreSelectedSignatoryType] = useState({
    value: 1,
    label: "1 to Sign",
  });
  const [preSelectedDurationType, setPreSelectedDurationType] = useState({
    value: 1,
    label: "Per Day",
  });
  const [options, setOptions] = useState([
    { value: "John Doe", label: "John Doe" },
    { value: "Jane Smith", label: "Jane Smith" },
    { value: "Michael Lee", label: "Michael Lee" },
  ]);
  const [members, setMembers] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([
    options[0],
    options[1],
  ]);
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={props.isSelected}
            onChange={() => null} // handled by react-select
            className="flex-shrink-0 custom-checkbox"
          />
          <span className="truncate">{props.label}</span>
        </div>
      </components.Option>
    );
  };

  // Recursively build nodes & edges from API data
  const [graphNodes, setGraphNodes] = useState([]);
  const [graphEdges, setGraphEdges] = useState([]);

  useEffect(() => {
    const n = [];
    const e = [];
    let idCounter = 0;

    const buildGraph = (node, parentId = null, depth = 0, xOffset = 0) => {
      const id = `${idCounter++}`;
      n.push({
        id,
        data: { label: `${node.name} (${node.type})` },
        position: { x: xOffset * 250, y: depth * 150 },
      });
      if (parentId) {
        e.push({
          id: `${parentId}-${id}`,
          source: parentId,
          target: id,
          animated: true,
        });
      }
      node.children?.forEach((child, idx) =>
        buildGraph(
          child,
          id,
          depth + 1,
          xOffset + idx - node.children.length / 2
        )
      );
    };

    data.forEach((root, idx) => buildGraph(root, null, 0, idx * 3)); // <== iterate roots
    setGraphNodes(n);
    setGraphEdges(e);
  }, [data]);

  // Parent state
  const [layers, setLayers] = useState([
    { name: "Interconnection Layer 01", shareholders: [] },
  ]);
  const [activeTabIndex, setActiveTabIndex] = useState({});
  const [isFocusedAccountFee, setIsFocusedAccountFee] = useState(false);
  const inputRefAccountFee = useRef(null);
  const optionsdate = [
    { label: "Never Expire", value: "never" },
    { label: "Set Custom Date", value: "custom" },
  ];

  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [customDate, setCustomDate] = useState("");
  const dropdownRef = useRef();
  const dateInputRef = useRef(null);
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
  };
  // parent component
  const addLayer = (qualified, parentName, afterLayerName) => {
    setLayers((prevLayers) => {
      // 1️⃣ Find index of the layer after which new layer should be added
      const idx = prevLayers.findIndex((l) => l.name === afterLayerName);

      // 2️⃣ Keep layers up to current layer
      const baseLayers = idx >= 0 ? prevLayers.slice(0, idx + 1) : [];

      // 3️⃣ Determine new layer number
      const newLayerNumber = baseLayers.length + 1;
      const newLayerName = `Interconnection Layer ${String(
        newLayerNumber
      ).padStart(2, "0")}`;

      // 4️⃣ Reset shareholder fields for next layer
      const resetShareholders = qualified.map((q) => ({
        id: Date.now() + Math.random(),
        name: q.name, // optional: keep name for tab label
        type: q.type || "Individual",
        shares: 0,
        email: "",
        errors: {},
      }));

      const newLayer = {
        name: newLayerName,
        shareholders: resetShareholders,
        parent: parentName,
      };

      return [...baseLayers, newLayer];
    });

    // 5️⃣ Reset active tab indices for trimmed layers and new layer
    setActiveTabIndex((prevTabs) => {
      const newLayerNumber = Object.keys(prevTabs).length + 1;
      const newLayerName = `Interconnection Layer ${String(
        newLayerNumber
      ).padStart(2, "0")}`;

      return {
        ...prevTabs,
        [afterLayerName]: 0, // reset current layer tab to first
        [newLayerName]: 0, // new layer starts at first tab
      };
    });
  };
  const formatNumber = (value) => {
    // Remove commas before parsing
    const number = parseFloat(value.toString().replace(/,/g, ""));

    if (isNaN(number)) return "";

    // Format with 2 decimal places and add commas
    return number.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };
  const countryOptions = [
    { code: "AU", name: "Australia", dial: "+61" },
    { code: "US", name: "United States", dial: "+1" },
    { code: "IN", name: "India", dial: "+91" },
    { code: "GB", name: "United Kingdom", dial: "+44" },
    // Add more countries as needed
  ];
  const handleSelect = (option) => {
    setSelected(option.value);
    setIsOpen(false);
    setCustomDate("");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const handleChangeAmountFee = (e, amountValue) => {
    e.preventDefault();
    const rawValue = e.target.value.replace(/,/g, ""); // Remove commas

    if (!isNaN(rawValue) || rawValue === "") {
      const cursorPosition = e.target.selectionStart;

      setminimumInvestment(rawValue); // Store raw value without commas
      // Wait for state update before restoring cursor position
      setTimeout(() => {
        if (inputRefAccountFee.current) {
          inputRefAccountFee.current.setSelectionRange(
            cursorPosition,
            cursorPosition
          );
        }
      }, 0);
    }
  };
  const DropdownIndicator = (props) => {
    return (
      <components.DropdownIndicator {...props}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-black" // 👈 black icon
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </components.DropdownIndicator>
    );
  };
  const customStyles = {
    clearIndicator: () => ({ display: "none" }), // removes the cross
    indicatorSeparator: () => ({ display: "none" }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: "white", // hover/focus
      color: "black",
      cursor: "pointer",
      ":active": {
        backgroundColor: "white", // prevent blue flash on click
      },
    }),
    control: (provided, state) => ({
      ...provided,
      borderColor: "#d1d5db", // Tailwind gray-300
      boxShadow: "none", // remove blue focus ring
      "&:hover": { borderColor: "#d1d5db" },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#FCFCFC",
      border: "1px solid #E5E7EB", // Tailwind gray-200
      borderRadius: "10px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#374151", // Tailwind gray-700
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#374151",
      ":hover": { backgroundColor: "white", color: "" }, // red remove hover
    }),
  };
  const signatoryTypeData = [
    {
      value: 1,
      label: "1 to Sign",
    },
    {
      value: 2,
      label: "More than 1 to Sign",
    },
    {
      value: 3,
      label: "All to Sign",
    },
  ];
  const limitData = [
    {
      value: 1,
      label: "Per Day",
    },
    {
      value: 2,
      label: "Per Week",
    },
    {
      value: 3,
      label: "Per Month",
    },
  ];

  useEffect(() => {
    setMembers((prev) => {
      if (prev.length === 0) return prev;

      return prev.map((member, i) => ({
        ...member,
        managingMember: i === 0 ? "yes" : "no", // first one selected by default
      }));
    });
  }, []); // run only on component mount
  useEffect(() => {
    setMembers((prev) => {
      // Check if any member is already selected
      const isAnySelected = prev.some((m) => m.managingMember === "yes");

      if (!isAnySelected && prev.length > 0) {
        // No selection yet → select the first member
        return prev.map((member, i) => ({
          ...member,
          managingMember: i === 0 ? "yes" : "no",
        }));
      }

      // Otherwise, keep existing selection
      return prev;
    });
  }, [members.length]); // watch only the length, not full array
  const updateMemberManaging = (index) => {
    setMembers((prev) =>
      prev.map((member, i) => ({
        ...member,
        managingMember: i === index ? "yes" : "no",
      }))
    );
  };
  const handleDeleteFile = () => {
    setFileAttorney(null);
  };
  const addMember = () => {
    // setisUserPopOpen(true);
    setMembers([
      ...members,
      {
        firstName: "",
        dob: "",
        email: "",
        countryCode: "AU(+61)",
        mobile: "",
        address: "",
        accountType: "individual",
        errors: {
          email: "",
          mobile: "",
        },
      },
    ]);
  };
  const removeMember = (index) => {
    const updated = members.filter((_, i) => i !== index);
    setMembers(updated);
  };
  const updateMember = (index, key, value, newErrors = null) => {
    setMembers((prev) =>
      prev.map((member, i) =>
        i === index
          ? {
              ...member,
              [key]: value,
              errors: newErrors !== null ? newErrors : member.errors,
            }
          : member
      )
    );
  };
  const handleSelectSignatory = (selectedOption) => {
    console.log("Selected Option:", selectedOption.value);
    setSignatoryId(selectedOption.value);
    setSignatoryText(selectedOption.label);
  };
  const handleFileChangeAttorney = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileType = selectedFile.type;
      const fileSize = selectedFile.size;
      if (
        ["application/pdf", "image/png", "image/jpeg"].includes(fileType) &&
        fileSize <= 10 * 1024 * 1024
      ) {
        setFileAttorney(selectedFile);
      } else {
        showToast(
          "File must be PDF, PNG, or JPEG and less than 10MB.",
          "error"
        );
      }
    }
  };

  const handleReset = () => {
    setCurrentStep(1);
  };
  const handleBack = () => {
    setCurrentStep(1);
  };
  const handleContinue = () => {
    // your logic like setCurrentStep(2), API calls, etc.
    handlesave();
    setCurrentStep(3);
  };
  const handleSelectSignatoryType = (selectedOption) => {
    console.log("Selected Option:", selectedOption.value);
    setSignatoryType(selectedOption.value);
    setSignatoryTypeLabel(selectedOption.label);
    if (selectedOption.value == 3) {
      setSelectedOptions(options);
    }
  };
  const handleSelectDurationType = (selectedOption) => {
    console.log("Selected Option:", selectedOption.value);
    setLimitDurationId(selectedOption.value);
    setLimitDurationLabel(selectedOption.label);
  };
  const handlesave = () => {
    dispatch(updateFormData({ field: "members", value: members }));
    dispatch(updateFormData({ field: "shareholderLayers", value: layers }));
  };

  return (
    <div className="flex flex-col min-h-[calc(100vh-100px)] lg:min-w-[80vw] lg:max-w-[80vw]">
      <div className="flex-grow  overflow-visible mb-4">
        {loading && (
          <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-[999]">
            <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
        <div className="bg-white shadow-md rounded-md p-4">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between border-b border-colour-custom px-4 pb-2 mb-4 gap-2">
            <div className="flex items-center gap-2">
              <img src="/people.svg" alt="User Avatar" className="h-6 w-6" />
              <h1 className="text-xl font-medium">
                Directors:{" "}
                <span className="text-[#8C8C8C] font-medium">Phinix LLC</span>
              </h1>
            </div>
            <div className="flex flex-col sm:flex-row gap-1 sm:gap-2 items-start sm:items-center">
              <span className="text-sm text-labelColour">
                Number of directors within your organisation:
              </span>
              <span className="text-sm font-medium text-black">
                <div className="font-semibold">
                  <div className="flex items-center text-sm mt-1 md:mt-0 space-x-0 ml-1 border  rounded-md bg-white shadow-md">
                    {/* Remove last row */}
                    <button
                      onClick={() => {
                        setMembers((prev) => {
                          if (prev.length === 0) return prev; // avoid error if no rows
                          return prev.slice(0, prev.length - 1); // remove last member
                        });
                      }}
                      className="text-black px-2"
                    >
                      <Minus size={18} />
                    </button>

                    {/* Count display */}
                    <span className="text-sm font-medium bg-black text-white p-2">
                      {members.length.toString().padStart(2, "0")}
                    </span>

                    {/* Add new row */}
                    <button onClick={addMember} className="text-black px-2">
                      <Plus size={18} />
                    </button>
                  </div>
                </div>
              </span>
            </div>
          </div>

          {/* Info Alert */}
          <div className="bg-[#FFC421] bg-opacity-50 border-2 border-[#FFC421] text-[#363636] text-[10px] rounded-lg px-2 py-1 mb-4 w-full sm:w-auto">
            Information of all the Directors must be added. Please ensure before
            submitting the form. Failing to do so will lead in resubmitting the
            application.
          </div>

          {/* Directors Table */}
          <div className="overflow-x-auto">
            <table className="min-w-[800px] w-full text-sm text-left border rounded overflow-hidden">
              <thead className="bg-[#F9F9F9] text-labelColour font-semibold">
                <tr>
                  <th className="p-2 w-[40px]"></th>
                  <th className="p-2">First Name</th>
                  <th className="p-2">Last Name</th>
                  <th className="p-2">Email Address</th>
                  <th className="p-2">Mobile Number</th>
                  <th className="p-2">Managing Member</th>
                  <th className="p-2 w-[40px]"></th>
                </tr>
              </thead>
              <tbody>
                {members.map((member, idx) => (
                  <tr key={idx} className="border-t bg-white align-top">
                    <td className="p-2 font-medium text-labelColour">
                      {(idx + 1).toString().padStart(2, "0")}.
                    </td>
                    <td className="p-2">
                      <input
                        className="border w-full p-2 rounded-xl text-sm"
                        placeholder="First Name"
                        value={member.firstName}
                        onChange={(e) =>
                          updateMember(idx, "firstName", e.target.value)
                        }
                      />
                    </td>
                    <td className="p-2">
                      <input
                        className="border w-full p-2 rounded-xl text-sm"
                        placeholder="Last Name"
                        value={member.lastName}
                        onChange={(e) =>
                          updateMember(idx, "lastName", e.target.value)
                        }
                      />
                    </td>
                    <td className="p-2">
                      <div className="flex flex-col">
                        <input
                          className={`border w-full p-2 rounded-xl text-sm ${
                            member.errors?.email ? "border-red-500" : ""
                          }`}
                          type="email"
                          placeholder="Email"
                          value={member.email}
                          onChange={(e) => {
                            const value = e.target.value;
                            const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                              value
                            );
                            updateMember(idx, "email", value, {
                              ...member.errors,
                              email:
                                isValid || value === ""
                                  ? ""
                                  : "Invalid email address",
                            });
                          }}
                        />
                        {member.errors?.email && (
                          <span className="text-red-500 text-xs mt-1">
                            {member.errors.email}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-2">
                      <div className="flex flex-col">
                        <div
                          className={`flex items-center border rounded-xl overflow-hidden ${
                            member.errors?.mobile ? "border-red-500" : ""
                          }`}
                        >
                          <select
                            className="px-2 text-sm text-[#888888] font-medium bg-transparent outline-none"
                            value={member.countryCode}
                            onChange={(e) =>
                              updateMember(idx, "countryCode", e.target.value)
                            }
                          >
                            {countryOptions.map((c) => (
                              <option key={c.code} value={c.dial}>
                                {c.code} ({c.dial})
                              </option>
                            ))}
                          </select>
                          <input
                            className={`outline-none p-2 text-sm w-full ${
                              member.errors?.mobile ? "border-red-500" : ""
                            }`}
                            placeholder="Mobile"
                            value={member.mobile}
                            onChange={(e) => {
                              const value = e.target.value;
                              const isValid = validator.isMobilePhone(
                                member.countryCode + member.mobile,
                                "any"
                              );
                              updateMember(idx, "mobile", value, {
                                ...member.errors,
                                mobile:
                                  isValid || value === ""
                                    ? ""
                                    : "Enter a valid mobile number",
                              });
                            }}
                          />
                        </div>
                        {member.errors?.mobile && (
                          <span className="text-red-500 text-xs mt-1">
                            {member.errors.mobile}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-2 flex justify-center items-center">
                      <input
                        type="radio"
                        name="managingMember"
                        value="yes"
                        checked={member.managingMember === "yes"}
                        onChange={() => updateMemberManaging(idx)}
                        className="w-4 h-4 confirm-check border-gray-300 focus:ring-[#005a9c]"
                      />
                    </td>
                    {idx !== 0 && (
                      <td className="p-2 text-center">
                        <button
                          onClick={() => removeMember(idx)}
                          title="Remove"
                          className="text-red-500"
                        >
                          <img
                            src="/trash.svg"
                            alt="Delete"
                            className="h-4 w-4"
                          />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Add Director Button */}
          <div className="px-4 py-3 flex justify-center">
            <button
              onClick={addMember}
              className="flex items-center gap-2 text-[#005A9C] rounded-md font-medium text-sm hover:underline border-2 border-dashed p-2"
            >
              <img src="/add-darkBlue.svg" alt="Add Icon" className="w-5 h-5" />
              Add Director
            </button>
          </div>

          {/* Save Button */}
          <div className="flex justify-end w-full mt-4">
            <button
              onClick={(e) => e.preventDefault()}
              className={`${
                members.length == 0 ||
                members.some(
                  (member) =>
                    member.errors &&
                    Object.values(member.errors).some(
                      (error) => error && error !== ""
                    )
                ) ||
                (members.length > 0 && members[0].email === "")
                  ? "bg-gray-300  cursor-not-allowed"
                  : "bg-black hover:bg-gray-900"
              } w-full md:w-auto  text-white font-medium py-2 px-4 rounded-full text-sm`}
            >
              Save
            </button>
          </div>

          {/* Signatory Section */}
          {members.length > 0 && (
            <div className="mt-4 border-t grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Signatory Type */}
              <div className="flex flex-col px-2 pt-4">
                <Dropdown
                  label={
                    <>
                      Select Signatory Type{" "}
                      <span className="text-red-500">*</span>
                    </>
                  }
                  options={signatoryTypeData}
                  onSelect={handleSelectSignatoryType}
                  preselectedValue={preSelectedSignatoryType}
                />
              </div>

              {/* Signatories */}
              <div className="flex flex-col px-2 pt-4 border-t md:border-t-0 md:border-l border-gray-300">
                {signatoryType === 1 && (
                  <Dropdown
                    label={
                      <>
                        Select Signatory <span className="text-red-500">*</span>
                      </>
                    }
                    options={signatoriesData}
                    onSelect={handleSelectSignatory}
                  />
                )}

                {signatoryType === 2 && (
                  <div className="mt-2">
                    <label className="block mb-2 text-sm text-gray-700">
                      Select Signatories <span className="text-red-500">*</span>
                    </label>
                    <Select
                      options={options}
                      isMulti
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      components={{ Option, DropdownIndicator }}
                      onChange={setSelectedOptions}
                      allowSelectAll={true}
                      value={selectedOptions}
                      className="text-sm rounded-md w-full"
                      placeholder=""
                      styles={customStyles}
                    />
                    {selectedOptions.length === 0 && (
                      <label className="block mt-1 text-xs text-[#FF5050] flex items-center">
                        <img
                          src="/danger.svg"
                          alt="alert Icon"
                          className="h-4 w-4 mr-1"
                        />
                        Please select at least one signatory.
                      </label>
                    )}
                  </div>
                )}

                {signatoryType === 3 && (
                  <div className="mt-2">
                    <label className="block mb-2 text-sm text-gray-700">
                      Select Signatories <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-1 border rounded-md border-gray-300 p-2 flex flex-wrap gap-2">
                      {options.map((opt, index) => (
                        <div
                          key={index}
                          className="flex items-center bg-[#FCFCFC] text-gray-700 text-sm px-3 py-1 rounded-lg border shadow-sm"
                        >
                          <span>{opt.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* File Upload */}
              {signatoryType === 1 && (
                <div className="flex flex-col px-2 pt-4">
                  <label className="border-2 border-dashed rounded-lg p-6 block text-center cursor-pointer flex flex-col items-center">
                    <input
                      type="file"
                      id="fileUpload"
                      accept=".pdf,.png,.jpeg"
                      onChange={handleFileChangeAttorney}
                      className="hidden"
                    />
                    <img
                      src="/folder-add.svg"
                      alt="Upload Icon"
                      className="h-4 w-4"
                    />
                    <p className="mt-2 text-gray-500 text-sm">
                      Drag & Drop <br /> or{" "}
                      <span className="underline">Click to Upload</span>
                    </p>
                  </label>
                  <div className="mt-2 flex gap-2 flex-wrap text-xs">
                    {["PNG", "JPEG", "PDF", "< 10 MB"].map((item, idx) => (
                      <span
                        key={idx}
                        className="bg-[#8E8E8E] text-white px-2 py-1 rounded-md"
                      >
                        {item}
                      </span>
                    ))}
                  </div>

                  {fileAttorney && (
                    <div className="mt-4 bg-white border shadow-md rounded-lg flex items-center justify-between px-3 py-2">
                      <div className="flex flex-col">
                        <span className="text-black">{fileAttorney.name}</span>
                        <span className="text-[#46DA55] text-xs">
                          Upload Completed
                        </span>
                      </div>
                      <button
                        onClick={handleDeleteFile}
                        className="text-red-500 flex-shrink-0 font-semibold"
                      >
                        <img
                          src="/trash.svg"
                          alt="Delete"
                          className="h-4 w-4"
                        />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {formData.companyTypeId == 2 && (
            <>
              <div className="flex items-center border-b border-colour-custom space-x-2 px-4">
                <h1 className="text-lg font-medium mb-2">Transaction Limit</h1>
              </div>
              <div className="mt-4 border-t grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Signatory Type */}
                <div className="flex flex-col px-2 pt-4">
                  <Dropdown
                    label={
                      <>
                        Duration <span className="text-red-500">*</span>
                      </>
                    }
                    options={limitData}
                    onSelect={handleSelectDurationType}
                    preselectedValue={preSelectedDurationType}
                  />
                </div>

                <div className="flex flex-col px-2 pt-4 md:border-r md:border-t-0 md:border-l border-gray-300">
                  <div>
                    <label
                      htmlFor="amountInDollars"
                      className="block dark:text-gray-300 text-labelColour  color-test text-sm mb-1"
                    >
                      Amount in Dollars ($){" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="amountInDollars"
                      value={
                        isFocusedAccountFee
                          ? minimumInvestment
                          : formatNumber(minimumInvestment)
                      }
                      ref={inputRefAccountFee}
                      onChange={(e) => {
                        if (!isFocusedAccountFee) return;
                        handleChangeAmountFee(e, e.target.value);
                      }}
                      onFocus={() => setIsFocusedAccountFee(true)}
                      onBlur={() => setIsFocusedAccountFee(false)}
                      className="rounded-md bg-white  border border-gray-300  p-2 w-full text-black font-medium outline-none text-md"
                    />
                  </div>
                </div>

                <div className="flex flex-col px-2 pt-4">
                  <div className="w-full max-w-xs relative" ref={dropdownRef}>
                    <label className="block mb-2 text-sm text-gray-700">
                      Transaction Limit Expiry Date{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    {/* Main Box */}
                    <div className="font-medium relative w-full border border-gray-300 rounded-md flex items-center bg-white">
                      {/* Visible Input (styled like text, no default calendar) */}
                      <input
                        type="text"
                        readOnly
                        value={
                          selected === "custom"
                            ? formatDate(customDate) || "DD/MM/YYYY"
                            : selected === "never"
                            ? "Never Expire"
                            : ""
                        }
                        placeholder="DD/MM/YYYY"
                        className="w-full px-3 py-2 text-sm rounded-md bg-white text-black  cursor-default"
                      />

                      {/* Hidden Date Input (used for real selection) */}
                      <input
                        ref={dateInputRef}
                        type="date"
                        value={customDate}
                        onChange={(e) => setCustomDate(e.target.value)}
                        className="absolute right-8 top-1/2 -translate-y-1/2 opacity-0 w-6 h-6 cursor-pointer"
                      />
 
                      {/* Calendar Icon (only when "custom" is selected) */}
                      {selected === "custom" && (
                        <button
                          type="button"
                          onClick={() => dateInputRef.current.showPicker()}
                          className="absolute right-8 top-1/2 -translate-y-1/2 p-1"
                        >
                          <img
                            src="/calendar.svg"
                            alt="Calendar Icon"
                            className="w-4 h-4"
                          />
                        </button>
                      )}

                      {/* Dropdown Arrow */}
                      <button
                        type="button"
                        onClick={() => setIsOpen(!isOpen)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 h-5 w-5"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-4 h-4 text-black"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.23 7.21a.75.75 0 011.06-.02L10 10.69l3.71-3.5a.75.75 0 111.04 1.08l-4 3.75a.75.75 0 01-1.04 0l-4-3.75a.75.75 0 01-.02-1.06z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>

                    {/* Dropdown Options */}
                    {isOpen && (
                      <div className="absolute left-0 top-full mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg z-[9999]">
                        {optionsdate.map((option) => (
                          <div
                            key={option.value}
                            onClick={() => handleSelect(option)}
                            className="px-3 py-2 cursor-pointer hover:bg-black hover:text-white text-sm"
                          >
                            {option.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
          {isUserPopOpen && (
            <AddUserPopup
              setactualData={setactualData}
              onClose={() => setisUserPopOpen(false)}
            />
          )}
        </div>
        {formData.companyTypeId == 1 && (
          <div className="bg-white shadow-md rounded-md p-4 mt-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-2 md:space-y-0 px-4">
              <div className="flex items-center space-x-2">
                <img src="/people.svg" alt="User Avatar" className="h-6 w-6" />
                <h1 className="text-xl font-medium">
                  Shareholders:
                  <span className="text-xl font-medium text-[#8C8C8C] ml-1">
                    Phinix LLC
                  </span>
                </h1>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setShowVisualLink(true);
                  }}
                  className="bg-white text-black text-sm px-3 py-2 rounded-xl border shadow-md flex items-center space-x-2"
                >
                  <img
                    src="/hashtag.svg"
                    alt="Collapse Icon"
                    className="w-4 h-4 flex-shrink-0"
                  />
                  <span className="font-medium">Visual Link</span>
                </button>
                <button
                  onClick={handleCollapseAll}
                  className="bg-white text-black text-sm px-3 py-2 rounded-xl border shadow-md flex items-center space-x-2"
                >
                  <img
                    src="/arrow-square-up.svg"
                    alt="Collapse Icon"
                    className="w-4 h-4 flex-shrink-0"
                  />
                  <span className="font-medium">Collapse All</span>
                </button>
                <button
                  onClick={handleExpandAll}
                  className="bg-black text-white text-sm px-3 py-2 rounded-xl border shadow-md flex items-center space-x-2"
                >
                  <img
                    src="/arrow-square-down.svg"
                    alt="Collapse Icon"
                    className="w-4 h-4 flex-shrink-0"
                  />
                  <span className="font-medium">Expand All</span>
                </button>
              </div>
            </div>
        {showVisualLink && (
  <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
      <div className="relative bg-white rounded-2xl shadow-2xl w-[95%] md:w-[900px] p-6">
        {/* Close Button */}
        <button
          onClick={() => setShowVisualLink(false)}
          className="absolute top-3 right-3 text-gray-600 hover:text-black text-2xl font-bold transition-colors"
        >
          &times;
        </button>

        {/* Graph */}
        <div className="mt-6 w-full h-[500px] border border-gray-200 rounded-xl overflow-hidden">
          <ReactFlow
            nodes={graphNodes.map((n) => ({
              ...n,
              style: getNodeStyle(n)
            }))}
            edges={graphEdges.map((e) => ({
              ...e,
              animated: true,
              style: { stroke: "#6366f1", strokeWidth: 2 },
              markerEnd: { type: "arrowclosed", color: "#6366f1" }
            }))}
            defaultEdgeOptions={{ type: "smoothstep" }}
            nodesDraggable={false}
            panOnScroll
            zoomOnScroll
            fitView
          >
            {/* Soft dotted background */}
            <Background gap={24} color="rgba(0,0,0,0.06)" variant="dots" />
            <Controls className="bg-white/80 rounded-md shadow-md" />
          </ReactFlow>
        </div>
      </div>
    </div>
)}

            <ul className="space-y-2 pl-6 pr-2 text-xs font-medium text-black list-disc">
              <li>Please provide shareholder details for your company here.</li>
              <li>
                If a corporate entity or trust holds 25% or more shares, we'll
                create interconnection layers to identify the shareholders of
                those entities. This will continue until we identify the
                individual ultimate beneficial owner of your company.
              </li>
              <li>
                Please complete the application for any corporate or trust
                shareholder who owns 25% or more shares. Failure to do so will
                prevent you from continuing the application.
              </li>
              <li>
                The application for the individual ultimate beneficial owners
                will be sent to the email address you provide here, To obtain
                the additional information Caprock requires.
              </li>
            </ul>
            <div className="p-6">
              {layers.map((layer, i) => (
                <ShareholderLayer
                  key={i}
                  layerName={layer.name}
                  shareholdersForLayer={layer.shareholders}
                  onAddLayer={addLayer}
                  collapsed={collapsedLayers[layer.name] ?? false} // controlled
                  onToggle={() =>
                    setCollapsedLayers((prev) => ({
                      ...prev,
                      [layer.name]: !prev[layer.name],
                    }))
                  }
                />
              ))}
            </div>
          </div>
        )}
      </div>
      <footer className=" mt-4 flex flex-col flex-shrink-0 md:flex-row p-4 bg-[#005A9C] z-50">
        <div className="flex flex-col md:flex-row items-center justify-start w-full space-y-2 md:space-y-0">
          <ConfirmResetButton onConfirm={handleReset} onBack={handleBack} />
        </div>
        <div className="bottom-actions flex flex-col md:flex-row mt-2 md:mt-0 items-center justify-end w-full space-y-2 md:space-y-0 md:space-x-4">
          <div className="w-full md:w-auto flex justify-center md:justify-end">
            <button className="flex w-full md:w-auto items-center justify-center space-x-2 text-white border-white border-2 font-medium py-2 px-4 rounded-full text-sm">
              <span>Save as Draft</span>
            </button>
          </div>
          <ConfirmContinueButton
            onContinue={handleContinue}
            buttonText="Continue"
            confirmTitle="Save Section"
            confirmMessage="Are you sure, you want to continue. This action will ensure, the saved information is un-editable. Therefore please make sure you have provided the correct details."
          />
        </div>
      </footer>
    </div>
  );
};

export default DirectorInfo;
